import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  CircularProgress,
  Alert,
  MenuItem,
  Grid,
  Divider,
} from "@mui/material";
import { ArrowBack, Save } from "@mui/icons-material";
import { Layout } from "../components/Layout";
import {
  clientService,
  ClientDetails,
  UpdateClientData,
  EntityType,
} from "../services/client.service";
import { toLocaleDateString } from "../utils/dateUtils";

const ClientDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [client, setClient] = useState<ClientDetails | null>(null);
  const [entityTypes, setEntityTypes] = useState<EntityType[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  // Form state
  const [formData, setFormData] = useState<UpdateClientData>({});

  const loadClient = async () => {
    if (!id) return;

    setLoading(true);
    setError("");

    try {
      const data = await clientService.getClientById(parseInt(id));
      setClient(data);
      setFormData({
        companyName: data.companyName,
        emailAddress: data.emailAddress,
        address1: data.address1,
        address2: data.address2 || "",
        city: data.city,
        state: data.state,
        zipcode: data.zipcode,
        country: data.country,
        phone1: data.phone1,
        phone2: data.phone2 || "",
        fax: data.fax || "",
        typeID: data.typeID,
        statusID: data.statusID,
      });
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to load client");
    } finally {
      setLoading(false);
    }
  };

  const loadEntityTypes = async () => {
    try {
      const types = await clientService.getEntityTypes();
      setEntityTypes(types);
    } catch (err) {
      console.error("Failed to load entity types:", err);
    }
  };

  useEffect(() => {
    loadClient();
    loadEntityTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleSave = async () => {
    if (!id) return;

    setSaving(true);
    setError("");
    setSuccessMessage("");

    try {
      await clientService.updateClient(parseInt(id), formData);
      setSuccessMessage("Client updated successfully");
      await loadClient(); // Reload to get updated data
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to update client");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <Box display='flex' justifyContent='center' p={4}>
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  if (!client) {
    return (
      <Layout>
        <Alert severity='error'>Client not found</Alert>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box>
        <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate("/clients")}
            sx={{ mr: 2 }}
          >
            Back to Clients
          </Button>
          <Typography variant='h4' sx={{ flexGrow: 1 }}>
            {client.companyName}
          </Typography>
          <Button
            variant='contained'
            startIcon={<Save />}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </Box>

        {error && (
          <Alert severity='error' sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {successMessage && (
          <Alert severity='success' sx={{ mb: 2 }}>
            {successMessage}
          </Alert>
        )}

        {/* Company Information */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant='h6' gutterBottom>
            Company Information
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label='Company Name'
                value={formData.companyName || ""}
                onChange={(e) =>
                  setFormData({ ...formData, companyName: e.target.value })
                }
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label='Email Address'
                type='email'
                value={formData.emailAddress || ""}
                onChange={(e) =>
                  setFormData({ ...formData, emailAddress: e.target.value })
                }
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label='Entity Type'
                value={formData.typeID || ""}
                onChange={(e) =>
                  setFormData({ ...formData, typeID: parseInt(e.target.value) })
                }
              >
                {entityTypes.map((type) => (
                  <MenuItem key={type.id} value={type.id}>
                    {type.description}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label='Status'
                value={formData.statusID || 1}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    statusID: parseInt(e.target.value),
                  })
                }
              >
                <MenuItem value={1}>Active</MenuItem>
                <MenuItem value={2}>Inactive</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label='Address Line 1'
                value={formData.address1 || ""}
                onChange={(e) =>
                  setFormData({ ...formData, address1: e.target.value })
                }
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label='Address Line 2'
                value={formData.address2 || ""}
                onChange={(e) =>
                  setFormData({ ...formData, address2: e.target.value })
                }
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label='City'
                value={formData.city || ""}
                onChange={(e) =>
                  setFormData({ ...formData, city: e.target.value })
                }
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label='State'
                value={formData.state || ""}
                onChange={(e) =>
                  setFormData({ ...formData, state: e.target.value })
                }
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label='Zip Code'
                value={formData.zipcode || ""}
                onChange={(e) =>
                  setFormData({ ...formData, zipcode: e.target.value })
                }
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label='Phone'
                value={formData.phone1 || ""}
                onChange={(e) =>
                  setFormData({ ...formData, phone1: e.target.value })
                }
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label='Phone 2'
                value={formData.phone2 || ""}
                onChange={(e) =>
                  setFormData({ ...formData, phone2: e.target.value })
                }
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label='Fax'
                value={formData.fax || ""}
                onChange={(e) =>
                  setFormData({ ...formData, fax: e.target.value })
                }
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Primary Contact Information */}
        {client.primaryContact && (
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant='h6' gutterBottom>
              Primary Contact
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant='body2' color='text.secondary'>
                  Name
                </Typography>
                <Typography variant='body1'>
                  {client.primaryContact.firstname}{" "}
                  {client.primaryContact.lastname}
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant='body2' color='text.secondary'>
                  Email
                </Typography>
                <Typography variant='body1'>
                  {client.primaryContact.emailAddress}
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant='body2' color='text.secondary'>
                  Phone
                </Typography>
                <Typography variant='body1'>
                  {client.primaryContact.phone1}
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant='body2' color='text.secondary'>
                  Title
                </Typography>
                <Typography variant='body1'>
                  {client.primaryContact.title || "-"}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        )}

        {/* Additional Information */}
        <Paper sx={{ p: 3 }}>
          <Typography variant='h6' gutterBottom>
            Additional Information
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant='body2' color='text.secondary'>
                Client ID
              </Typography>
              <Typography variant='body1'>{client.id}</Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant='body2' color='text.secondary'>
                Created Date
              </Typography>
              <Typography variant='body1'>
                {toLocaleDateString(client.createdDate)}
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant='body2' color='text.secondary'>
                Entity Type
              </Typography>
              <Typography variant='body1'>
                {client.typeName || "Unknown"}
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant='body2' color='text.secondary'>
                Status
              </Typography>
              <Typography variant='body1'>
                {client.statusName || "Active"}
              </Typography>
            </Grid>
          </Grid>
        </Paper>
      </Box>
    </Layout>
  );
};

export default ClientDetailPage;
